import { buttonClasses } from '@mui/material';
import type { Theme, ButtonProps } from '@mui/material';

import { UI_BUTTON_SECONDARY } from '../../constants/uiConstants';
import { goPalette, tsGoUnit } from '../tsGoThemeVars';

function getButtonContainedStyle(ownerState: ButtonProps, theme: Theme) {
	const defaultStyles = {
		borderRadius: tsGoUnit.tsUnit,
		textDecoration: 'none !important',
		boxShadow: 'none !important',
		padding: '6px 16px',
		textTransform: 'uppercase',
		fontWeight: 600,
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
	};

	if (ownerState.color === UI_BUTTON_SECONDARY) {
		return {
			...defaultStyles,
			backgroundColor: goPalette.tsGoColor.white,
			border: `1px solid ${goPalette.tsGoColor.grayLight()}`,
			textTransform: 'uppercase',

			'&:hover': {
				backgroundColor: goPalette.tsGoColor.grayLight(0.5),
			},
		};
	}

	// default primary style
	return {
		...defaultStyles,
		backgroundColor: goPalette.tsGoColor.pink,
		color: 'white',
		'&:hover': {
			backgroundColor: goPalette.tsGoColor.pinkDarker,
		},
	};
}

function getButtonOutlinedStyle(type, theme) {
	switch (type) {
		case 'warning': {
			return {
				backgroundColor: 'transparent',
				border: '1px solid hsl(0, 100%, 37%)',
				borderRadius: '20px',
				color: 'hsl(0, 100%, 37%)',
				textDecoration: 'none !important',
				width: '100%',
				textTransform: 'uppercase',
				justifyContent: 'center',
				'&:hover': {
					border: '1px solid hsl(0, 100%, 37%)',
					color: 'hsl(0, 100%, 37%)',
				},
			};
		}
		default: {
			return {
				border: `1px solid ${goPalette.tsGoColor.gray} !important`,
				color: goPalette.tsGoColor.black,
				textDecoration: 'none !important',
				backgroundColor: goPalette.tsGoColor.white,
				width: '100%',
				justifyContent: 'space-between',
				fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.mini),
				textTransform: 'none',
				whiteSpace: 'nowrap',
			};
		}
	}
}

function getActionButtonStyle(theme: Theme) {
	// this is the 3-dots button which usually opens an aside
	return {
		backgroundColor: 'transparent',
		color: theme.palette.tsGoColor.slateLighter,

		'&:hover': {
			backgroundColor: 'transparent',
			textDecoration: 'none !important',
		},
	};
}

function getPagerButtonStyle(theme: Theme) {
	// this renders the pager button between the gotToFirst goToPrev
	// and goToNext and goToLast pages
	// << < [1 2 3] > >>
	return {
		backgroundColor: 'transparent',
		color: theme.palette.tsGoColor.blue,
		minWidth: 'auto',
		fontWeight: theme.typography.tsFontWeight.semiBold,

		'&:hover': {
			backgroundColor: 'transparent',
			textDecoration: 'none !important',
			color: theme.palette.tsGoColor.blueDark,
		},
		'&:disabled': {
			color: theme.palette.tsGoColor.black,
		},
	};
}

function getconButtonStyle(theme: Theme) {
	return {
		color: theme.palette.tsGoColor.slateLighter,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 'unset',
		height: '30px',
		padding: '0',
		marginLeft: 'auto',
		marginRight: 'auto',

		'&:hover': {
			color: theme.palette.tsGoColor.black,
			textDecoration: 'none !important',
			backgroundColor: 'transparent',
		},

		[`.${buttonClasses.startIcon}`]: {
			fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.icon),
			marginRight: 0,
			marginLeft: 0,
			display: 'flex',
		},

		'&::before': {
			content: 'none',
		},
	};
}

function getTsBtnAddStyle(theme: Theme) {
	return {
		color: theme.palette.tsGoColor.blue,
		fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.small),
		fontWeight: theme.typography.tsFontWeight.semiBold,
		textTransform: 'uppercase',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',

		'&:hover': {
			color: theme.palette.tsGoColor.blueDark,
			textDecoration: 'none !important',
			backgroundColor: 'transparent',
		},
	};
}

function getHamburgerStyles(theme: Theme) {
	return {
		border: '0',
		height: theme.tsGoUnit.tsUnitDouble,
		width: theme.tsGoUnit.tsUnitDouble,
		padding: `${theme.tsGoUnit.tsUnitHalf} ${theme.tsGoUnit.tsUnitHalf} ${theme.tsGoUnit.tsUnitHalf} 0`,
		color: theme.palette.tsGoColor.black,
		cursor: 'pointer',
		transition: 'background-color 0.2s ease-in-out',
		minWidth: 'auto',
		display: 'block',
		[theme.breakpoints.up('md')]: {
			display: 'none',
		},
	};
}

export const muiButtonOverrides = {
	styleOverrides: {
		root: ({ ownerState, theme }) => ({
			variants: [
				{
					props: {
						'ts-variant': 'hamburger',
					},
					style: getHamburgerStyles(theme),
				},
				{
					props: {
						variant: 'contained',
					},
					style: getButtonContainedStyle(ownerState, theme),
				},
				{
					props: {
						variant: 'outlined',
					},
					style: getButtonOutlinedStyle(ownerState.color, theme),
				},
				{
					props: {
						'ts-variant': 'actionButton',
						variant: 'text',
					},
					style: getActionButtonStyle(theme),
				},
				{
					props: {
						'ts-variant': 'pagerButton',
						variant: 'text',
					},
					style: getPagerButtonStyle(theme),
				},
				{
					props: {
						'ts-variant': 'tsIcon',
						variant: 'text',
					},
					style: getconButtonStyle(theme),
				},
				{
					props: {
						'ts-variant': 'tsBtnAdd',
						variant: 'text',
					},
					style: getTsBtnAddStyle(theme),
				},
			],
		}),
	},
};
